import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState } from 'react';
import axios from "axios";
import { Alert, Spin } from "antd";
import RegisterCustomerForm from './RegisterCustomerForm';
import RegisterContractorForm from './RegisterContractorForm';
import { axiosErrorHandler, isDuplicateError } from "../../swzutils";
import { dispatchLoginResponseV6Router, performUsernamePasswordLogin } from '../login/PerformLogin';
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { PARAM_WATCH_GETTING_STARTED_INTRO_VIDEO } from "../app/routes/home/HomePage";
import { useAppDispatch } from "../../store";
const INVITE_PARAM = "invite";
const INVITE_STORAGE_KEY = "registration_invite_code";
const defaultChannel = "marketplace";
const channelTitles = {
    [defaultChannel]: "Marketplace Registration",
    "dms": "Delivery Management Registration",
    "contractor": "Contractor Registration"
};
const RegisterPage = () => {
    const navigate = useNavigate();
    const params = useParams();
    const [searchParams] = useSearchParams();
    const [embedded, setEmbedded] = useState(searchParams.get("embedded") || false);
    const [registering, setRegistering] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [heightChangeTimerId, setHeightChangeTimerId] = useState(undefined);
    const [lastHeight, setLastHeight] = useState(0);
    const dispatch = useAppDispatch();
    useEffect(() => {
        loadGoogleCaptcha();
        const form = document.getElementById("subtrux-registration-form");
        if (embedded && form) {
            // HACK ALERT:  Only way I could find to watch for height changes was to use a timer
            setHeightChangeTimerId(setInterval(listenForHeightChanges, 500));
        }
        return () => {
            heightChangeTimerId && clearInterval(heightChangeTimerId);
            setHeightChangeTimerId(undefined);
        };
    }, [embedded]);
    const getHeight = () => {
        const form = document.getElementById("subtrux-registration-form");
        if (form) {
            return Math.max(form.clientHeight, form.scrollHeight, form.offsetHeight);
        }
        return 700;
    };
    const listenForHeightChanges = () => {
        const height = getHeight();
        if (lastHeight !== height) {
            setLastHeight(height);
            notifyEmbedParent("APPLY_HEIGHT", {
                height
            });
        }
    };
    const loadGoogleCaptcha = () => {
        const script = document.createElement('script');
        script.src = "//www.google.com/recaptcha/api.js";
        script.async = true;
        script.defer = true;
        /*script.addEventListener('load', () => {
            this.setState({ captchaReady: true });
        });*/
        document.body.appendChild(script);
    };
    const onError = (errMsge) => {
        setErrorMessage(errMsge);
        setRegistering(false);
        // @ts-ignore
        if (grecaptcha && grecaptcha.reset) {
            // @ts-ignore
            grecaptcha.reset();
        }
    };
    const performRegistration = (values) => {
        setErrorMessage(null);
        setRegistering(true);
        axios.post('/admin/v1/tenants/registrations', {
            channel: determineChannel(),
            companyName: values.companyName,
            firstName: values.firstName,
            lastName: values.lastName,
            contactTelephone: values.contactTelephone,
            contactMobile: values.contactMobile,
            contactEmail: values.contactEmail,
            addressStreet: values.addressStreet,
            addressSuburb: values.addressSuburb,
            addressState: values.addressState,
            addressPostcode: values.addressPostcode,
            addressCountryCode: "AU",
            bankName: values.bankName,
            bankAccountName: values.bankAccountName,
            bankBsb: values.bankBsb,
            bankAccountNo: values.bankAccountNo,
            abn: values.abn,
            industry: values.industry,
            industryOther: values.industryOther,
            password: values.password,
            referredByCode: values.referredByCode || extractAndSaveInviteCode(),
            categories: values.vehicleCategories,
            captchaChallengeResponse: values.captchaChallengeResponse
        }).then(( /*response*/) => {
            execPerformLogin(values.contactEmail, values.password);
        }).catch((error) => {
            if (isDuplicateError(error)) {
                goToRegistrationExistsPage();
            }
            else {
                axiosErrorHandler(error, onError);
            }
        });
    };
    const execPerformLogin = (username, password) => {
        performUsernamePasswordLogin(username, password, null, true, (loginResponse, errorMessage, requiresOTP) => {
            setErrorMessage(errorMessage);
            if (requiresOTP) {
                // should never happen (at time of writing) since this is a new registration
                // and we do not require otp on new registrations but just in case redirect
                // to login page where otp is handled.
                navigate("/login");
            }
            if (embedded) {
                const port = location.port ? ':' + location.port : '';
                const subTruxAppUrl = location.protocol + '//' + location.hostname + port + `/#/app/home?${PARAM_WATCH_GETTING_STARTED_INTRO_VIDEO}=1`;
                notifyEmbedParent("REGISTRATION_COMPLETE", {
                    subTruxAppUrl
                });
            }
            else if (loginResponse && !requiresOTP) {
                setRegistering(false);
                dispatchLoginResponseV6Router(loginResponse, navigate, searchParams.get("toUrl"), dispatch);
            }
        });
    };
    const notifyEmbedParent = (subTruxEventName, data) => {
        parent.postMessage({
            subTruxEventName,
            ...data
        }, '*');
    };
    const goToRegistrationExistsPage = () => {
        let registrationExistsUri = "/registration-exists";
        if (embedded) {
            registrationExistsUri += "?embedded=1";
        }
        navigate(registrationExistsUri);
    };
    const determineChannel = () => {
        const channel = params.channel;
        return channel && channelTitles[channel] ? channel : defaultChannel;
    };
    const extractAndSaveInviteCode = () => {
        const browserSupportsLocalStorage = localStorage && typeof localStorage.getItem === "function";
        if (!browserSupportsLocalStorage) {
            return null;
        }
        const inviteCode = searchParams.get(INVITE_PARAM);
        if (inviteCode && inviteCode.length > 0) {
            localStorage.setItem(INVITE_STORAGE_KEY, inviteCode);
            return inviteCode;
        }
        return localStorage.getItem(INVITE_STORAGE_KEY);
    };
    const channel = determineChannel();
    const inviteCode = extractAndSaveInviteCode();
    const title = channelTitles[channel];
    const className = channel === "contractor" ? "page-register-contractor" : "page-register-customer";
    const registrationForm = (_jsx(Spin, { spinning: registering, children: _jsxs("div", { id: "subtrux-registration-form", children: [errorMessage &&
                    _jsx(Alert, { title: "Registration Failed", description: errorMessage, type: "error", showIcon: true }), channel === "contractor" ?
                    _jsx(RegisterContractorForm, { onDoRegister: performRegistration, inviteCode: inviteCode })
                    :
                        _jsx(RegisterCustomerForm, { onDoRegister: performRegistration, inviteCode: inviteCode, channel: channel })] }) }));
    if (embedded) {
        return registrationForm;
    }
    return (_jsxs("div", { className: `page-public ${className}`, children: [_jsx("div", { className: "logo text-center", children: _jsx("img", { width: 190, height: 50, src: "/assets/images/subtrux-logo-black.png" }) }), _jsxs("div", { className: "main-body", children: [_jsx("h2", { children: title }), _jsx("div", { className: "body-form", children: registrationForm })] })] }));
};
export default RegisterPage;
